import type { ProblemTimer } from './types';

export interface ProblemTimerOptions {
  mode?: 'timer' | 'countdown';
  countdownDurationMs?: number;
}

export function createProblemTimer(opts: ProblemTimerOptions = {}): ProblemTimer {
  return {
    state: 'idle',
    startedAt: null,
    stoppedAt: null,
    elapsedMs: 0,
    mode: opts.mode ?? 'timer',
    countdownDurationMs: opts.countdownDurationMs ?? 0
  };
}

export function startProblem(
  timer: ProblemTimer,
  opts: ProblemTimerOptions = {}
): ProblemTimer {
  if (timer.state !== 'idle') return timer;
  return {
    state: 'running',
    startedAt: Date.now(),
    stoppedAt: null,
    elapsedMs: 0,
    mode: opts.mode ?? timer.mode,
    countdownDurationMs: opts.countdownDurationMs ?? timer.countdownDurationMs
  };
}

export function stopProblem(
  timer: ProblemTimer,
  opts: { clampToCountdown?: boolean } = {}
): ProblemTimer {
  if (timer.state !== 'running' || timer.startedAt === null) return timer;
  const stoppedAt = Date.now();
  let elapsedMs = stoppedAt - timer.startedAt;
  if (opts.clampToCountdown && timer.mode === 'countdown' && timer.countdownDurationMs > 0) {
    elapsedMs = Math.min(elapsedMs, timer.countdownDurationMs);
  }
  return {
    state: 'evaluating',
    startedAt: timer.startedAt,
    stoppedAt,
    elapsedMs,
    mode: timer.mode,
    countdownDurationMs: timer.countdownDurationMs
  };
}

export function remainingCountdownMs(timer: ProblemTimer): number {
  if (timer.mode !== 'countdown' || timer.countdownDurationMs <= 0) return 0;
  const elapsed = liveElapsedMs(timer);
  return Math.max(0, timer.countdownDurationMs - elapsed);
}

export function tickCountdown(timer: ProblemTimer): { expired: boolean; timer: ProblemTimer } {
  if (timer.state !== 'running' || timer.mode !== 'countdown') {
    return { expired: false, timer };
  }
  return { expired: remainingCountdownMs(timer) === 0, timer };
}

export function resetProblem(timer: ProblemTimer): ProblemTimer {
  return createProblemTimer({
    mode: timer.mode,
    countdownDurationMs: timer.countdownDurationMs
  });
}

export function freezeForMacroPause(timer: ProblemTimer): ProblemTimer {
  if (timer.state !== 'running' || timer.startedAt === null) return timer;
  const stoppedAt = Date.now();
  return {
    state: 'evaluating',
    startedAt: timer.startedAt,
    stoppedAt,
    elapsedMs: stoppedAt - timer.startedAt,
    mode: timer.mode,
    countdownDurationMs: timer.countdownDurationMs
  };
}

export function liveElapsedMs(timer: ProblemTimer): number {
  if (timer.state === 'running' && timer.startedAt !== null) {
    return Date.now() - timer.startedAt;
  }
  return timer.elapsedMs;
}

export function formatProblemDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) ms = 0;
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const secStr = seconds.toFixed(1).padStart(4, '0');
  return `${minutes}:${secStr}`;
}